import React, {useState} from "react"
import {useModal} from "react-modal-hook"
import {useNavigate} from "react-router-dom"
import {OkDocument} from "../../resources/common"
import {IndividualApplication, IndividualApplicationData} from "../../resources/application"
import ReevaluateIndividualApplicationConfirmationModal from "../../components/ReevaluateApplication/ReevaluateConfirmationModal/ReevaluateApplicationConfirmationModal/ReevaluateIndividualApplicationConfirmationModal"
import ApplicationDetails, {identificationFieldBeforeUpdateRequest} from "./ApplicationDetails"
import {MetaItemProps} from "../../components/TitleBar/TitleBar"
import {ApplicationResubmitForms} from "./ReevaluatePage"
import ReevaluateIndividualForm, {
	ReevaluatedIndividualApplicationData,
} from "../../components/ReevaluateApplication/Forms/ReevaluateIndividualForm/ReevaluateIndividualForm"
import moment from "moment/moment"

interface ReevaluateIndividualPageProps {
	application: OkDocument<IndividualApplication>
	enableReevaluateApplication: boolean
}

export default function ReevaluateIndividualApplicationContainer({
	application,
	enableReevaluateApplication,
}: ReevaluateIndividualPageProps) {
	const navigate = useNavigate()

	const [individualReevaluatedData, setIndividualReevaluatedData] = useState<IndividualApplicationData>(
		application.data.attributes
	)

	const [showConfirmationModal, closeConfirmationModal] = useModal(
		() => (
			<ReevaluateIndividualApplicationConfirmationModal
				onSuccess={() => navigate(`/applications/${application.data.id}`)}
				close={closeConfirmationModal}
				application={application.data}
				enableReevaluateApplication={enableReevaluateApplication}
				reevaluatedData={individualReevaluatedData}
			/>
		),
		[individualReevaluatedData]
	)

	const isSoleProp = application.data.attributes.soleProprietorship
	const {first, last} = application.data.attributes.fullName
	const title = `${first} ${last}`
	const meta: MetaItemProps[] = [{text: "Individual", icon: "user-geometric-action---users"}]

	return (
		<ApplicationDetails
			title={title}
			meta={meta}
			application={application}
			formId={ApplicationResubmitForms.individualForm}
		>
			<ReevaluateIndividualForm
				formId={ApplicationResubmitForms.individualForm}
				application={application}
				submitCallback={(values: ReevaluatedIndividualApplicationData) => {
					const currentValues = {
						ssn: application.data.attributes.ssn,
						passport: application.data.attributes.passport,
						matriculaConsular: application.data.attributes.matriculaConsular,
					}
					const identificationFields = identificationFieldBeforeUpdateRequest({
						currentValues: currentValues,
						updatedValues: values,
					})

					// Update state and trigger the confirmation modal
					setIndividualReevaluatedData({
						...values,
						...identificationFields,
						nationality: values.nationality ? values.nationality : undefined,
						dob: values.dateOfBirth ? moment(values.dateOfBirth).toDate() : undefined,
						businessIndustry: values.businessIndustry === "" || !isSoleProp ? undefined : values.businessIndustry,
						sourceOfFunds: values.sourceOfFunds === "" ? undefined : values.sourceOfFunds,
						sourceOfFundsDescription:
							values.sourceOfFundsDescription === "" ? undefined : values.sourceOfFundsDescription,
						accountPurpose: values.accountPurpose === "" ? undefined : values.accountPurpose,
						accountPurposeDetail: values.accountPurposeDetail === "" ? undefined : values.accountPurposeDetail,
						transactionVolume: values.transactionVolume === "" ? undefined : values.transactionVolume,
						transactionVolumeDescription:
							values.transactionVolumeDescription === "" ? undefined : values.transactionVolumeDescription,
						profession: values.profession === "" || isSoleProp ? undefined : values.profession,
						usNexus: values.usNexus?.length && isSoleProp ? values.usNexus : undefined,
						isIncorporated: values.isIncorporated,
						stateOfIncorporation:
							values.stateOfIncorporation === "" || !isSoleProp ? undefined : values.stateOfIncorporation,
						yearOfIncorporation:
							values.yearOfIncorporation === "" || !isSoleProp ? undefined : values.yearOfIncorporation,
						countriesOfOperation:
							values.countriesOfOperation?.length && isSoleProp ? values.countriesOfOperation : undefined,
						businessDescription:
							values.businessDescription === "" || !isSoleProp ? undefined : values.businessDescription,
					})
					showConfirmationModal()
				}}
			/>
		</ApplicationDetails>
	)
}
